import {
  Archive,
  CalendarDays,
  Download,
  FileText,
  FilePlus2,
  LayoutTemplate,
  Network,
  PanelLeft,
  Search,
  Settings,
  Sparkles,
  SquareCheckBig,
  StickyNote,
  Trash2,
  Upload,
  UserCircle2,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { IconButton } from "@/ui/IconButton";
import { LogoMark } from "@/ui/Logo";
import { useUi } from "@/store/ui";
import { usePalette } from "@/store/palette";
import { createNote } from "@/commands/nav";
import { openDailyNote } from "@/commands/templates";
import { openCards } from "@/commands/cards";
import { openGraph } from "@/commands/graph";
import { useProjectChat } from "@/components/ProjectChatDialog";
import { useAnyRunning } from "@/store/chat";
import { useAgentEnabled } from "@/data/agentConfig";
import { exportZip } from "@/data/export";
import { useAuth } from "@/data/auth";
import { useSettingsDialog } from "@/components/SettingsDialog";
import { useImportDialog } from "@/components/ImportDialog";
import { useArchiveDialog } from "@/components/ArchiveDialog";
import { useTrashDialog } from "@/components/TrashDialog";
import { useSignInDialog } from "@/components/SignInDialog";
import { toast } from "@/ui/toast";
import s from "./Rail.module.css";

export function Rail() {
  const anyRunning = useAnyRunning();
  const agentEnabled = useAgentEnabled();
  const { activeApp, treeCollapsed, toggleTree, setActiveApp, setCardMode } = useUi();
  const openPalette = usePalette((p) => p.open);

  return (
    <nav className={s.rail} aria-label="Primary">
      <div className={s.logo}>
        <LogoMark size={22} />
      </div>

      <IconButton rail label="Notes" tooltipSide="right" active={activeApp === "notes"} onClick={() => setActiveApp("notes")}>
        <FileText size={16} />
      </IconButton>
      <IconButton
        rail
        label="Tasks"
        kbd="Mod+Shift+T"
        tooltipSide="right"
        active={activeApp === "tasks"}
        onClick={() => setActiveApp("tasks")}
      >
        <SquareCheckBig size={16} />
      </IconButton>
      <IconButton rail label="Cards" kbd="Mod+Shift+K" tooltipSide="right" active={activeApp === "cards"} onClick={openCards}>
        <StickyNote size={16} />
      </IconButton>
      <IconButton
        rail
        label="Graph"
        kbd="Mod+Shift+G"
        tooltipSide="right"
        active={activeApp === "graph"}
        onClick={openGraph}
      >
        <Network size={16} />
      </IconButton>

      <div className={s.divider} />

      <IconButton rail label="Find note" kbd="Mod+P" tooltipSide="right" onClick={() => openPalette("switcher")}>
        <Search size={16} />
      </IconButton>
      <IconButton rail label="New note" kbd="Mod+N" tooltipSide="right" onClick={() => createNote(null)}>
        <FilePlus2 size={16} />
      </IconButton>
      <IconButton rail label="New from template" kbd="Mod+Alt+N" tooltipSide="right" onClick={() => openPalette("template")}>
        <LayoutTemplate size={16} />
      </IconButton>
      <IconButton rail label="Daily note" kbd="Mod+D" tooltipSide="right" onClick={() => void openDailyNote()}>
        <CalendarDays size={16} />
      </IconButton>
      {agentEnabled ? (
        <IconButton
          rail
          label="Ask Claude"
          kbd="Mod+J"
          tooltipSide="right"
          onClick={() => useProjectChat.getState().setOpen(true)}
        >
          <Sparkles size={16} />
          {anyRunning ? <span className={s.runDot} /> : null}
        </IconButton>
      ) : null}

      <div className={s.divider} />

      <IconButton rail label="Import" tooltipSide="right" onClick={() => useImportDialog.getState().setOpen(true)}>
        <Upload size={16} />
      </IconButton>
      <IconButton
        rail
        label="Export all notes"
        tooltipSide="right"
        onClick={() => void exportZip(null).then((n) => toast.success(`Exported ${n} notes`))}
      >
        <Download size={16} />
      </IconButton>
      <IconButton rail label="Archive" tooltipSide="right" onClick={() => activeApp === "cards" ? setCardMode("archive") : useArchiveDialog.getState().setOpen(true)}>
        <Archive size={16} />
      </IconButton>
      <IconButton rail label="Trash" tooltipSide="right" onClick={() => activeApp === "cards" ? setCardMode("trash") : useTrashDialog.getState().setOpen(true)}>
        <Trash2 size={16} />
      </IconButton>

      <div className={s.spacer} />

      <AccountButton />
      <IconButton
        rail
        label={treeCollapsed ? "Show sidebar" : "Hide sidebar"}
        kbd="Mod+B"
        tooltipSide="right"
        active={!treeCollapsed}
        onClick={toggleTree}
      >
        <PanelLeft size={16} />
      </IconButton>
      <IconButton
        rail
        label="Settings"
        kbd="Mod+,"
        tooltipSide="right"
        onClick={() => useSettingsDialog.getState().setOpen(true)}
      >
        <Settings size={16} />
      </IconButton>
    </nav>
  );
}

function AccountButton() {
  const { status, user, signOut } = useAuth();

  if (status !== "signedIn" || !user) {
    return (
      <IconButton
        rail
        label="Sign in to back up your work"
        tooltipSide="right"
        onClick={() => useSignInDialog.getState().setOpen(true)}
      >
        <UserCircle2 size={16} />
      </IconButton>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <span>
          <IconButton rail label={user.email} tooltipSide="right" active>
            <UserCircle2 size={16} />
          </IconButton>
        </span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="hn-menu" side="right" align="end" sideOffset={6}>
          <div className="hn-menu-label">{user.email}</div>
          <DropdownMenu.Item
            className="hn-menu-item"
            onSelect={() => useSettingsDialog.getState().setOpen(true)}
          >
            Account and storage
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="hn-menu-sep" />
          <DropdownMenu.Item className="hn-menu-item" onSelect={() => void signOut()}>
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
